import { base64ToArrayBuffer, getExtension } from "../../../helpers";
// @ts-ignore - vendored MSG parser without type declarations
import MSGReader from "./msgReader/msgReader";

export interface IParsedMsgAttachment {
  name: string;
  url: string;
  contentType: string;
  /** Raw base64 content of the inner attachment. */
  contentBytes: string | null;
  /** True when the inner attachment is itself an Outlook .msg file. */
  isMsg: boolean;
  size: number;
}

export interface IParsedMsg {
  subject: string;
  senderName: string;
  senderEmail: string;
  recipients: { name?: string; email?: string }[];
  bodyHTML: string | null;
  body: string;
  attachments: IParsedMsgAttachment[];
}

/**
 * The vendored MSG reader returns fixed-length string fields that can contain
 * trailing NUL / control characters. These corrupt filenames, download
 * attributes and extension detection, so strip them before use.
 */
const sanitizeString = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }

  let result = "";
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    // Skip C0 control chars (incl. NUL) and DEL.
    if (code > 31 && code !== 127) {
      result += value[i];
    }
  }

  return result.trim();
};

const guessMimeType = (fileName: string, fallback?: string): string => {
  const extension = getExtension(fileName);

  // Prefer a reliable extension-based type. Only trust the reader-provided
  // MIME type when the extension is unknown (or the reader gave us something
  // more specific than a generic octet-stream).
  const cleanedFallback = sanitizeString(fallback);
  const map: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    bmp: "image/bmp",
    webp: "image/webp",
    svg: "image/svg+xml",
    msg: "application/vnd.ms-outlook",
    txt: "text/plain",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };

  if (map[extension]) {
    return map[extension];
  }

  if (cleanedFallback && cleanedFallback !== "application/octet-stream") {
    return cleanedFallback;
  }

  return "application/octet-stream";
};

const uint8ToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const chunkSize = 0x8000;

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }

  return window.btoa(binary);
};

/**
 * Parses an Outlook `.msg` file into headers, body and inner attachments.
 * Each attachment carries base64 content and a ready-to-use object URL so it
 * can be previewed or downloaded like any other attachment.
 */
export const parseMsgArrayBuffer = (
  arrayBuffer: ArrayBuffer,
): IParsedMsg | null => {
  try {
    const reader = MSGReader(arrayBuffer);
    const msg = reader.getFileData();

    const attachments: IParsedMsgAttachment[] = (msg?.attachments ?? []).map(
      (attachment: any, index: number) => {
        const rawName =
          sanitizeString(attachment?.fileName) ||
          sanitizeString(attachment?.fileNameShort) ||
          "";
        const rawExtension = sanitizeString(attachment?.extension);
        // Ensure the name carries an extension so preview/download work.
        const fileName =
          rawName ||
          `attachment-${index + 1}${rawExtension ? `.${rawExtension.replace(/^\./, "")}` : ""}`;
        const isMsg =
          Boolean(attachment?.innerMsgContent) ||
          getExtension(fileName) === "msg" ||
          rawExtension.replace(/^\./, "").toLowerCase() === "msg";
        // The vendored reader exposes the MIME type as `mimeType` (0x370e).
        const contentType = guessMimeType(fileName, attachment?.mimeType);

        let attachmentBase64: string | null = null;
        let url = "#";
        let size = attachment?.contentLength ?? 0;

        try {
          const extracted = reader.getAttachment(index);
          const content = extracted?.content;
          // Normalise whatever the reader returns (Uint8Array, ArrayBuffer,
          // number[]) into a fresh Uint8Array backed by its own ArrayBuffer.
          let raw: Uint8Array;
          if (content instanceof Uint8Array) {
            raw = new Uint8Array(content); // copy to detach from parser buffer
          } else if (content instanceof ArrayBuffer) {
            raw = new Uint8Array(content);
          } else if (Array.isArray(content)) {
            raw = Uint8Array.from(content);
          } else {
            raw = new Uint8Array(0);
          }

          size = raw.length || size;
          // Build the Blob straight from the bytes — no base64 round-trip,
          // which avoids any encoding corruption of binary content.
          url = URL.createObjectURL(new Blob([raw], { type: contentType }));
          // base64 is only needed to re-feed nested `.msg` attachments and for
          // the email preview flow; compute it from the same bytes.
          attachmentBase64 = uint8ToBase64(raw);
        } catch {
          attachmentBase64 = null;
        }

        return {
          name: fileName,
          contentType,
          contentBytes: attachmentBase64,
          url,
          isMsg,
          size,
        };
      },
    );

    return {
      subject: sanitizeString(msg?.subject) || "No Subject",
      senderName: sanitizeString(msg?.senderName) || "Unknown Sender",
      senderEmail: sanitizeString(msg?.senderEmail),
      recipients: (msg?.recipients ?? []).map((recipient: any) => ({
        name: sanitizeString(recipient?.name),
        email: sanitizeString(recipient?.email),
      })),
      bodyHTML: msg?.bodyHTML ?? null,
      body: msg?.body ?? "",
      attachments,
    };
  } catch {
    return null;
  }
};

/** Convenience wrapper for parsing a `.msg` provided as base64. */
export const parseMsgBase64 = (contentBytes: string): IParsedMsg | null => {
  try {
    return parseMsgArrayBuffer(base64ToArrayBuffer(contentBytes));
  } catch {
    return null;
  }
};
